/**
 * Συρτάρι ταμείου — μετάφραση ανάμεσα στη ΜΙΑ στήλη της βάσης και τις ΔΥΟ
 * ερωτήσεις της οθόνης (migration 230).
 *
 * ΓΙΑΤΙ ΞΕΧΩΡΙΣΤΟ ΑΡΧΕΙΟ: η στήλη `printers.cash_drawer` είναι μία τιμή
 * ('off' | 'pin2' | 'pin5' | 'both'), αλλά στην οθόνη είναι δύο διαφορετικές
 * αποφάσεις από δύο διαφορετικούς ανθρώπους: ο ιδιοκτήτης απαντά «ανοίγει ή
 * όχι;» και ο τεχνικός «σε ποιο pin είναι καλωδιωμένο;». Η μετάφραση είναι
 * καθαρή συνάρτηση για να ελέγχεται χωρίς να στηθεί ολόκληρη η οθόνη.
 *
 * ΠΡΟΣΟΧΗ: ο server αποφασίζει ΑΝ θα ανοίξει (κανόνας χρημάτων: μόνο απόδειξη
 * πελάτη, μόνο μετρητά, ποτέ delivery, ποτέ επανεκτύπωση). Εδώ ρυθμίζεται μόνο
 * το ΠΩΣ ανοίγει (καλωδίωση). Τα δύο δεν ανακατεύονται.
 */

export type CashDrawerMode = 'off' | 'pin2' | 'pin5' | 'both';
export type CashDrawerPin = Exclude<CashDrawerMode, 'off'>;

/** Η σειρά που εμφανίζεται στο select: πρώτα το «αυτόματο», που δουλεύει παντού. */
export const CASH_DRAWER_PINS: CashDrawerPin[] = ['both', 'pin2', 'pin5'];

// Οι διάρκειες του παλμού (cash_drawer_on_ms / off_ms) δεν ρυθμίζονται πια από την οθόνη (απόφαση
// ιδιοκτήτη 25/10/2026): μένουν ό,τι έχει ο εκτυπωτής στη βάση (CHECK του 230, έλεγχος της route).

/** Η πρώτη έκδοση agent που ξέρει το `openCashDrawer`. Ο agent ΔΕΝ αυτο-ενημερώνεται. */
export const CASH_DRAWER_MIN_AGENT = '2.1.0';

const isPin = (value: unknown): value is CashDrawerPin =>
  CASH_DRAWER_PINS.indexOf(value as CashDrawerPin) !== -1;

/**
 * Από τη στήλη στην οθόνη. Άγνωστη/κενή τιμή (παλιός server που δεν επιστρέφει
 * καν τη στήλη, ή τιμή που δεν καταλαβαίνουμε) → ΚΛΕΙΣΤΟ: το συρτάρι με τα
 * χρήματα δεν ανοίγει ποτέ «κατά λάθος» επειδή η οθόνη μάντεψε.
 */
export const splitCashDrawer = (value: unknown): { enabled: boolean; pin: CashDrawerPin } => {
  if (isPin(value)) return { enabled: true, pin: value };
  return { enabled: false, pin: 'both' };
};

/**
 * Από την οθόνη στη στήλη. Όταν είναι κλειστό, η επιλογή pin ΔΕΝ χάνεται στο
 * state της φόρμας — απλώς δεν φτάνει στη βάση, ώστε ένα «άνοιξέ το ξανά» να
 * μη ζητάει δεύτερη φορά την καλωδίωση.
 */
export const joinCashDrawer = (enabled: boolean, pin: CashDrawerPin): CashDrawerMode =>
  enabled ? pin : 'off';

/**
 * Ξέρει ο agent αυτού του PC να ανοίξει συρτάρι;
 *
 * Δέχεται πρώτα τα caps (αν ο agent τα δηλώνει ρητά) και μετά πέφτει στην
 * έκδοση. `null` έκδοση = συνδεδεμένος agent που δεν στέλνει έκδοση, δηλαδή
 * παλιός (πριν την 2.0.0) → όχι.
 *
 * ΠΡΟΣΟΧΗ ΣΤΗ ΓΡΑΦΗ: το cap γράφεται με ΠΑΥΛΑ — 'cash-drawer', ακριβώς όπως το
 * στέλνει ο agent (print-agent/index.js:43 AGENT_CAPS, ίδια σύμβαση με τα
 * 'raster-inproc'/'logo-ref') και όπως φτάνει αυτούσιο ως εδώ (server.js:459
 * `data.caps.map(String)`, printers.js:950). Με κάτω παύλα ο έλεγχος δεν
 * πιάνει ΠΟΤΕ και μένει μόνο η σύγκριση έκδοσης: ένα 2.0.x με το συρτάρι
 * μέσα θα εμφανιζόταν ως «παλιός agent» και ο ιδιοκτήτης θα έκανε άσκοπη
 * επανεγκατάσταση σε 30 PC.
 */
export const agentSupportsCashDrawer = (
  agentVersion: string | null | undefined,
  caps?: string[] | null
): boolean => {
  if (Array.isArray(caps) && caps.indexOf('cash-drawer') !== -1) return true;
  if (!agentVersion) return false;
  const [major, minor] = agentVersion.split('.').map(part => parseInt(part, 10) || 0);
  if (major > 2) return true;
  return major === 2 && minor >= 1;
};
